import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ApexChart } from 'ng-apexcharts';
import { ChartOptions } from 'services/models/charts/ChartOptions';
import { FormMetricsThermometerChartData } from 'services/models/form-builder/form-metrics/chart-data/thermometer-chart-data.model';
import { FormMetricsCategoryClickEvent } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsRespondentAnswerItemModel } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsItemModel } from 'services/models/form-builder/form-metrics/form-metrics-item.model';
import { FormMetricsChartMapperService } from '../../services/form-metrics-chart-mapper.service';
import { FormMetricsLegendItem } from '../../utils/form-metrics-legend.util';
import {
  applyHighlightColors,
  buildCategoryClickEvent,
  buildHighlightStroke,
  findHighlightForItem,
  FormMetricsSliceMeta,
  resolveHighlightedIndexes,
} from '../../utils/form-metrics-drilldown.util';

@Component({
  selector: 'app-form-metrics-thermometer-widget',
  templateUrl: './form-metrics-thermometer-widget.component.html',
  styleUrls: ['./form-metrics-thermometer-widget.component.scss'],
})
export class FormMetricsThermometerWidgetComponent implements OnChanges {
  @Input() item!: FormMetricsItemModel;
  @Input() categoryClickEnabled = false;
  @Input() highlightAnswers: FormMetricsRespondentAnswerItemModel[] = [];
  @Output() categoryClick = new EventEmitter<FormMetricsCategoryClickEvent>();

  chartOptions: Partial<ChartOptions> | null = null;
  legendItems: FormMetricsLegendItem[] = [];

  constructor(private chartMapper: FormMetricsChartMapperService) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.rebuild();
  }

  private rebuild(): void {
    if (!this.item || this.item.totalAnswers <= 0) {
      this.chartOptions = null;
      this.legendItems = [];
      return;
    }

    const mapped = this.chartMapper.mapThermometer(this.item);
    const data = this.item.chartData as FormMetricsThermometerChartData;
    const distribution = [...(data?.distribution || [])].sort((a, b) => a.value - b.value);
    const slices: FormMetricsSliceMeta[] = distribution.map(d => ({
      optionId: d.optionId,
      label: d.label || String(d.value),
      value: d.value,
    }));
    const highlight = findHighlightForItem(this.highlightAnswers, this.item.formItemId);
    const highlightedIndexes = resolveHighlightedIndexes(slices, highlight);
    const baseColors = (mapped.chartOptions.colors as string[]) || [];

    const chart: ApexChart = {
      ...(mapped.chartOptions.chart as ApexChart),
    };
    if (this.categoryClickEnabled) {
      chart.events = {
        dataPointSelection: (_event, _ctx, config) => {
          const index = config?.seriesIndex;
          const slice = slices[index];
          if (!slice) {
            return;
          }
          this.categoryClick.emit(
            buildCategoryClickEvent(this.item.formItemId, this.item.chartType, slice)
          );
        },
      };
    }

    this.chartOptions = {
      ...mapped.chartOptions,
      colors: applyHighlightColors(baseColors, highlightedIndexes),
      chart,
    };

    const stroke = buildHighlightStroke(baseColors, highlightedIndexes);
    if (stroke) {
      this.chartOptions.stroke = { width: stroke.width, colors: stroke.colors };
    }

    this.legendItems = mapped.legendItems;
  }
}
